import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch
} from '@mui/material';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';

const STATUSES = ['open', 'in_progress', 'waiting_customer', 'resolved', 'closed'];
const PRIORITIES = ['low', 'normal', 'high', 'urgent'];

function statusColor(s) {
  if (s === 'open') return 'info';
  if (s === 'resolved' || s === 'closed') return 'success';
  if (s === 'waiting_customer') return 'warning';
  return 'primary';
}

export default function PlatformSupportTicketDetailPage() {
  const { ticketId } = useParams();
  const { user } = usePlatformAuth();
  const canManage = user && (user.role === 'super_admin' || user.role === 'support');

  const [detail, setDetail] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reply, setReply] = useState('');
  const [internalOnly, setInternalOnly] = useState(false);
  const [posting, setPosting] = useState(false);
  const [savingMeta, setSavingMeta] = useState(false);
  const [priority, setPriority] = useState('normal');
  const [status, setStatus] = useState('open');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [assignedTo, setAssignedTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/support-tickets/${ticketId}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Not found');
      setDetail(json);
      const t = json.ticket;
      setPriority(t.priority || 'normal');
      setStatus(t.status || 'open');
      setResolutionNotes(t.resolution_notes || '');
      setAssignedTo(t.assigned_to_user_id != null ? String(t.assigned_to_user_id) : '');
    } catch (e) {
      setError(e.message);
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    const tid = detail?.ticket?.tenant_id;
    if (!tid || !canManage) return undefined;
    (async () => {
      try {
        const res = await platformApiFetch(
          `/api/platform/tenant-users?tenantId=${tid}&pageSize=100&status=active&sort=email&order=asc`
        );
        const json = await res.json().catch(() => ({}));
        if (res.ok && !cancelled) setUsers(json.users || []);
      } catch (_e) {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [detail?.ticket?.tenant_id, canManage]);

  const saveMeta = async (e) => {
    e.preventDefault();
    setSavingMeta(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/support-tickets/${ticketId}`, {
        method: 'PATCH',
        body: {
          priority,
          status,
          resolution_notes: resolutionNotes,
          assigned_to_user_id: assignedTo === '' ? null : Number(assignedTo)
        }
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Update failed');
      setDetail(json);
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingMeta(false);
    }
  };

  const postReply = async (e) => {
    e.preventDefault();
    const text = reply.trim();
    if (!text) return;
    setPosting(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/support-tickets/${ticketId}/messages`, {
        method: 'POST',
        body: { body: text, is_internal: internalOnly }
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to post');
      setDetail(json);
      setReply('');
      setInternalOnly(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setPosting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !detail) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!detail?.ticket) return null;

  const t = detail.ticket;
  const messages = detail.messages || [];

  const row = (label, value) => (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1">{value ?? '—'}</Typography>
    </Grid>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4">{t.subject}</Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {t.ticket_number || `#${t.id}`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t.tenant_name} ({t.tenant_code})
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
            <Chip size="small" label={t.status.replace(/_/g, ' ')} color={statusColor(t.status)} />
            <Chip size="small" label={t.priority} variant="outlined" />
          </Box>
        </Box>
        <Button variant="outlined" component={Link} to="/platform/support-tickets">
          Back to list
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {canManage && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Update ticket
          </Typography>
          <Box component="form" onSubmit={saveMeta}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Priority</InputLabel>
                  <Select label="Priority" value={priority} onChange={(e) => setPriority(e.target.value)}>
                    {PRIORITIES.map((p) => (
                      <MenuItem key={p} value={p}>
                        {p}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                    {STATUSES.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s.replace(/_/g, ' ')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Assign (tenant user)</InputLabel>
                  <Select
                    label="Assign (tenant user)"
                    value={assignedTo}
                    onChange={(e) => setAssignedTo(e.target.value)}
                  >
                    <MenuItem value="">Unassigned</MenuItem>
                    {users.map((u) => (
                      <MenuItem key={u.id} value={String(u.id)}>
                        {u.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Resolution notes"
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  size="small"
                />
              </Grid>
            </Grid>
            <Button type="submit" variant="contained" sx={{ mt: 2 }} disabled={savingMeta}>
              {savingMeta ? 'Saving…' : 'Save changes'}
            </Button>
          </Box>
        </Paper>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          {row('Requester', t.requester_email)}
          {row('Assignee', t.assignee_email)}
          {row('Opened', t.opened_at ? String(t.opened_at) : '—')}
        </Grid>
        <Typography variant="subtitle2" sx={{ mt: 2 }}>
          Description
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {t.description || '—'}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom>
        Thread
      </Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        {messages.map((m) => (
          <Box key={m.id} sx={{ mb: 2 }}>
            <Typography variant="caption" color={m.is_internal ? 'warning.main' : 'text.secondary'}>
              {m.created_at ? String(m.created_at) : ''} · {m.author_type.replace(/_/g, ' ')}
              {m.author_email ? ` · ${m.author_email}` : ''}
              {m.is_internal ? ' · internal' : ''}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
              {m.body}
            </Typography>
            <Divider sx={{ mt: 2 }} />
          </Box>
        ))}

        {canManage && (
          <Box component="form" onSubmit={postReply} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Reply"
              value={reply}
              onChange={(e) => setReply(e.target.value)}
            />
            <FormControlLabel
              control={<Switch checked={internalOnly} onChange={(e) => setInternalOnly(e.target.checked)} />}
              label="Internal note (hidden from tenant)"
              sx={{ mt: 1, display: 'block' }}
            />
            <Button type="submit" variant="contained" sx={{ mt: 1 }} disabled={posting || !reply.trim()}>
              {posting ? 'Sending…' : 'Post'}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
